import { useState, useEffect, Fragment } from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { Dialog, Transition } from '@headlessui/react';
import { 
  ArrowTopRightOnSquareIcon, 
  ClipboardDocumentIcon,
  ArrowPathIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import PremiumFeatureModal from './PremiumFeatureModal';
import { useSubscription } from '../hooks/useSubscription';
import { track } from '../lib/track';

export default function CalendarSubscription() {
  const session = useSession();
  const queryClient = useQueryClient();
  const userId = session?.user?.id;
  const [feedUrl, setFeedUrl] = useState<string>('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
  const { hasFamilySync, isLoading: subscriptionLoading, refetch: refetchSubscription } = useSubscription();

  // Refetch subscription when component mounts or when user returns from payment
  useEffect(() => {
    if (!userId) return;

    // Check if we're coming from payment success
    const paymentSuccessFlag = sessionStorage.getItem('payment_success') === 'true';
    
    if (paymentSuccessFlag) {
      // Clear the flag immediately
      sessionStorage.removeItem('payment_success');
      
      // Refetch immediately, then again after a delay to catch webhook updates
      refetchSubscription();
      queryClient.invalidateQueries(['subscription', userId]);
      queryClient.invalidateQueries(['profile', userId]);
      
      // Also refetch after a delay to catch webhook processing
      const timer1 = setTimeout(() => {
        refetchSubscription();
        queryClient.invalidateQueries(['subscription', userId]);
        queryClient.invalidateQueries(['profile', userId]);
      }, 2000);
      
      // One more refetch after longer delay to be sure
      const timer2 = setTimeout(() => {
        refetchSubscription();
        queryClient.invalidateQueries(['subscription', userId]);
        queryClient.invalidateQueries(['profile', userId]);
      }, 5000);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [userId, refetchSubscription, queryClient]);

  // Also refetch when component becomes visible (user returns to tab)
  useEffect(() => {
    if (!userId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refetch subscription when user returns to the tab
        refetchSubscription();
        queryClient.invalidateQueries(['subscription', userId]);
        queryClient.invalidateQueries(['profile', userId]);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [userId, refetchSubscription, queryClient]);

  // Helper function to generate calendar feed URL
  // In development (localhost), use Supabase URL directly since calendar apps don't support webcal://localhost
  // In production, use cleaner Netlify redirect URL
  const getCalendarFeedUrl = (token: string): string => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (isLocalhost) {
      // Use Supabase Edge Function URL directly in development
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const projectId = supabaseUrl.split('//')[1].split('.')[0];
      return `webcal://${projectId}.supabase.co/functions/v1/calendar-feed/${token}`;
    } else {
      // Use cleaner URL via Netlify redirect in production
      const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
      return `webcal://${baseUrl.replace(/^https?:\/\//, '')}/calendar/feed/${token}`;
    }
  };

  // Fetch calendar feed token
  const { data: profile, isLoading, refetch } = useQuery(
    ['profile', userId],
    async () => {
      if (!userId) return null;
      
      // First check if profile exists
      let { data, error } = await supabase
        .from('profiles')
        .select('calendar_feed_token')
        .eq('id', userId)
        .single();
      
      // If profile doesn't exist, create it (trigger will auto-generate token)
      if (error && (error.code === 'PGRST116' || error.message?.includes('No rows returned'))) {
        const { data: newProfile, error: insertError } = await supabase
          .from('profiles')
          .insert({ id: userId })
          .select('calendar_feed_token')
          .single();
        
        if (insertError) throw insertError;
        return newProfile;
      } else if (error) {
        throw error;
      }
      
      // Return data as-is (don't auto-generate if token is null - user may have revoked it)
      return data;
    },
    {
      enabled: !!userId,
      onSuccess: (data) => {
        if (data?.calendar_feed_token) {
          const token = data.calendar_feed_token;
          setFeedUrl(getCalendarFeedUrl(token));
        } else {
          // Clear feedUrl if token is null (revoked)
          setFeedUrl('');
        }
      }
    }
  );

  const copyToClipboard = () => {
    handlePremiumAction(() => {
      if (!feedUrl) {
        toast.error('No calendar link available');
        return;
      }
      
      // Convert webcal:// to https:// for copying
      const httpsUrl = feedUrl.replace('webcal://', 'https://');
      navigator.clipboard.writeText(httpsUrl);
      toast.success('Calendar link copied!');
    });
  };

  const handlePremiumAction = async (action: () => void | Promise<void>) => {
    if (!session) {
      // Redirect to login if not authenticated
      window.location.href = '/login';
      return;
    }

    if (!hasFamilySync) {
      // Show premium modal if user doesn't have subscription
      setIsPremiumModalOpen(true);
      return;
    }

    // User has subscription, proceed with action
    try {
      const result = action();
      // Handle async actions
      if (result instanceof Promise) {
        await result;
      }
    } catch (error) {
      console.error('Error in premium action:', error);
      toast.error('An error occurred. Please try again.');
    }
  };

  const regenerateToken = async () => {
    if (!userId) return;

    const hadExistingFeed = !!feedUrl;

    await handlePremiumAction(async () => {
      try {
        // First, ensure profile exists
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', userId)
          .single();

        // If profile doesn't exist, create it
        if (!existingProfile) {
          const { error: insertError } = await supabase
            .from('profiles')
            .insert({ id: userId });
          
          if (insertError) throw insertError;
        }

        // Generate new token
        const newToken = crypto.randomUUID().replace(/-/g, '');
        
        const { data, error } = await supabase
          .from('profiles')
          .update({ 
            calendar_feed_token: newToken
          })
          .eq('id', userId)
          .select('calendar_feed_token')
          .single();
        
        if (error) throw error;
        
        if (data?.calendar_feed_token) {
          const token = data.calendar_feed_token;
          setFeedUrl(getCalendarFeedUrl(token));
          
          track(hadExistingFeed ? 'calendar_feed_link_regenerated' : 'calendar_feed_link_generated', {
            persona: 'parent',
            user_id: userId,
          });
          
          // Invalidate and refetch
          queryClient.invalidateQueries(['profile', userId]);
          
          toast.success('New calendar link generated! The old link is now invalid.');
        }
      } catch (error: any) {
        console.error('Error regenerating token:', error);
        toast.error(`Failed to regenerate calendar link: ${error.message || 'Unknown error'}`);
      }
    });
  };

  const revokeToken = async () => {
    if (!userId) return;

    setIsRevokeModalOpen(false);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          calendar_feed_token: null
        })
        .eq('id', userId);
      
      if (error) throw error;
      
      // Clear feedUrl immediately
      setFeedUrl('');
      
      track('calendar_feed_link_revoked', {
        persona: 'parent',
        user_id: userId,
      });
      
      // Invalidate and refetch to ensure UI updates
      await queryClient.invalidateQueries(['profile', userId]);
      await refetch();
      
      toast.success('Calendar link revoked. The feed is now disabled.');
    } catch (error) {
      console.error('Error revoking token:', error);
      toast.error('Failed to revoke calendar link');
    }
  };

  // Only show feed URL if user has Family Sync subscription
  const shouldShowFeedUrl = hasFamilySync && feedUrl;

  // Debug logging - MUST be before any early returns to maintain hook order
  useEffect(() => {
    console.log('CalendarSubscription status:', {
      hasFamilySync,
      feedUrl: !!feedUrl,
      shouldShowFeedUrl,
      userId,
      subscriptionLoading
    });
  }, [hasFamilySync, feedUrl, shouldShowFeedUrl, userId, subscriptionLoading]);

  if (isLoading || subscriptionLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-10 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!shouldShowFeedUrl) {
    return (
      <>
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h3 className="text-lg font-semibold mb-2 inline-flex items-center gap-2">
            <CalendarDaysIcon className="h-5 w-5 text-primary" />
            Subscribe to Your Family Calendar
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Generate a calendar feed link to subscribe to your family's camp schedule in Apple Calendar, Google Calendar, or Outlook.
          </p>
          <button
            onClick={() => regenerateToken()}
            className="btn btn-primary inline-flex items-center gap-1.5 text-sm sm:text-base"
          >
            <ArrowPathIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            Generate Calendar Link
          </button>
        </div>
        <PremiumFeatureModal
          isOpen={isPremiumModalOpen}
          onClose={() => setIsPremiumModalOpen(false)}
          featureName="Family Sync"
          upgradeUrl="/pricing/parents"
        />
      </>
    );
  }

  return (
    <>
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <h3 className="text-lg font-semibold mb-2 inline-flex items-center gap-2">
        <CalendarDaysIcon className="h-5 w-5 text-primary" />
        Subscribe to Your Family Calendar
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        Add this calendar to Apple Calendar, Google Calendar, or Outlook. 
        It automatically updates when you add or remove camps!
      </p>
      <p className="text-xs text-gray-500 mb-4">
        💡 <strong>Note:</strong> Calendar apps cache feeds for 5-15 minutes. If you make changes, refresh your calendar app or wait a few minutes for updates to appear.
      </p>
      
      <div className="flex flex-wrap gap-2 mb-4">
        {hasFamilySync ? (
          <>
            <a 
              href={feedUrl}
              className="btn btn-primary inline-flex items-center gap-1.5 text-sm sm:text-base"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              Subscribe in Calendar App
            </a>
            
            <button
              onClick={copyToClipboard}
              className="btn btn-secondary inline-flex items-center gap-1.5 text-sm sm:text-base"
            >
              <ClipboardDocumentIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              Copy Link
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setIsPremiumModalOpen(true)}
              className="btn btn-primary inline-flex items-center gap-1.5 text-sm sm:text-base"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              Subscribe in Calendar App
            </button>
            
            <button
              onClick={() => setIsPremiumModalOpen(true)}
              className="btn btn-secondary inline-flex items-center gap-1.5 text-sm sm:text-base"
            >
              <ClipboardDocumentIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              Copy Link
            </button>
          </>
        )}
      </div>

      <div className="mb-4">
        <button
          onClick={() => setShowInstructions(!showInstructions)}
          className="text-sm text-purple-600 hover:text-purple-700 inline-flex items-center gap-1"
        >
          {showInstructions ? (
            <>
              Hide Instructions
              <ChevronUpIcon className="h-4 w-4" />
            </>
          ) : (
            <>
              Show Instructions
              <ChevronDownIcon className="h-4 w-4" />
            </>
          )}
        </button>
        
        {showInstructions && (
          <div className="mt-3 p-4 bg-gray-50 rounded-lg text-sm space-y-3">
            <div>
              <strong>Apple Calendar:</strong>
              <ol className="list-decimal list-inside ml-2 mt-1 space-y-1">
                <li>Click "Subscribe in Calendar App"</li>
                <li>Apple Calendar will open</li>
                <li>Click "Subscribe" to confirm</li>
                <li><strong>To refresh:</strong> Right-click the calendar → "Refresh" or wait 5-15 minutes</li>
              </ol>
            </div>
            <div>
              <strong>Google Calendar:</strong>
              <ol className="list-decimal list-inside ml-2 mt-1 space-y-1">
                <li>Copy the calendar link</li>
                <li>Open Google Calendar</li>
                <li>Click the "+" next to "Other calendars"</li>
                <li>Select "From URL"</li>
                <li>Paste the link (change webcal:// to https://)</li>
                <li>Click "Add calendar"</li>
                <li><strong>To refresh:</strong> Click the 3 dots next to the calendar → "Refresh" or wait 5-15 minutes</li>
              </ol>
            </div>
            <div>
              <strong>Outlook:</strong>
              <ol className="list-decimal list-inside ml-2 mt-1 space-y-1">
                <li>Copy the calendar link</li>
                <li>Open Outlook Calendar</li>
                <li>Go to "File" → "Account Settings" → "Account Settings"</li>
                <li>Click "Internet Calendars" tab</li>
                <li>Click "New" and paste the link (change webcal:// to https://)</li>
                <li>Click "Add"</li>
                <li><strong>To refresh:</strong> Right-click the calendar → "Update" or wait 5-15 minutes</li>
              </ol>
            </div>
          </div>
        )}
      </div>

      {hasFamilySync && (
        <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
          <button
            onClick={() => regenerateToken()}
            className="btn btn-secondary inline-flex items-center gap-1.5 text-sm"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Regenerate Link
          </button>
          
          <button
            onClick={() => setIsRevokeModalOpen(true)}
            className="btn inline-flex items-center gap-1.5 text-sm text-red-600 bg-transparent border border-red-600 hover:bg-red-50"
          >
            <XMarkIcon className="h-4 w-4" />
            Revoke Link
          </button>
        </div>
      )}
    </div>
    
    {/* Revoke Confirmation Modal */}
    <Transition.Root show={isRevokeModalOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={() => setIsRevokeModalOpen(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                        Revoke Calendar Link
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to revoke your calendar link? This will disable the feed for anyone who has subscribed.{' '}
                          <span className="font-semibold text-gray-700">This action cannot be undone.</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                    onClick={revokeToken}
                  >
                    Revoke
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={() => setIsRevokeModalOpen(false)}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
    
    {/* Premium Feature Modal */}
    <PremiumFeatureModal
      isOpen={isPremiumModalOpen}
      onClose={() => setIsPremiumModalOpen(false)}
      featureName="Family Sync"
      upgradeUrl="/pricing/parents"
    />
    </>
  );
}

